import Checkbox from '@mui/material/Checkbox';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import React from 'react';
import { CheckboxWrapper } from '../../../design-system';
import type { FilterComponentProps } from './types';
import { accessibilityLabel } from './utils';

type SelectableFilterValues<V extends string | number> = Record<V, boolean>;

type Props<V extends string | number, T extends SelectableFilterValues<V>> = FilterComponentProps & {
  defaultFilters: T;
  renderValues: (values: [string, boolean][]) => string;
  options: [string, string][];
};

function areSelectableFilters<V extends string | number>(search: unknown): search is SelectableFilterValues<V> {
  if (typeof search !== 'object' || !search) {
    return false;
  }
  return Object.values(search).every((value) => typeof value === 'boolean');
}

function isArrayOfSelectableFilters(value: unknown): value is [string, boolean][] {
  if (!Array.isArray(value)) {
    return false;
  }

  return value.every(
    ([filterKey, filterApplied]) => typeof filterKey === 'string' && typeof filterApplied === 'boolean',
  );
}

export function filterBySelectableItem<T extends string | number>(search: unknown, { value }: { value: T }) {
  if (!areSelectableFilters(search)) {
    return false;
  }
  if (Object.values(search).every((v) => !v)) {
    return true;
  }
  return search[value];
}

// Keep filter cells narrow when many options are selected — the dropdown already shows every
// checked option, so the cell preview only needs enough to hint at the selection.
const MAX_VISIBLE_FILTER_VALUES = 2;

export function truncateAppliedFilters(labels: string[]): string {
  if (labels.length <= MAX_VISIBLE_FILTER_VALUES) return labels.join(', ');
  return `${labels.slice(0, MAX_VISIBLE_FILTER_VALUES).join(', ')}, +${labels.length - MAX_VISIBLE_FILTER_VALUES}`;
}

export function SelectableFilters<V extends string | number, T extends SelectableFilterValues<V>>({
  columnDef,
  defaultFilters,
  onFilterChanged,
  options,
  renderValues,
}: Props<V, T>) {
  const { tableData } = columnDef;

  const [filtersMenuOpen, setFiltersMenuOpen] = React.useState<boolean>(false);

  // Read the applied values straight from material-table's own filter state so the component
  // stays in sync if its local instance is ever recreated (e.g., on a sibling state change
  // that triggers material-table's internal setState). Previously this component owned the
  // applied values in a React.useState, which meant any remount reset every checkbox to false
  // and silently cleared the active filter.
  const storedFilters = tableData?.filterValue;
  const appliedFilters: T = areSelectableFilters<V>(storedFilters) ? (storedFilters as T) : defaultFilters;

  const toggleFilter = React.useCallback(
    (key: V) => {
      if (!tableData) {
        return;
      }
      onFilterChanged(tableData.id, { ...appliedFilters, [key]: !appliedFilters[key] });
    },
    [appliedFilters, onFilterChanged, tableData],
  );

  React.useEffect(() => {
    if (!tableData) {
      return;
    }
    onFilterChanged(tableData.id, appliedFilters);
  }, [appliedFilters, onFilterChanged, tableData]);

  if (!tableData) {
    return null;
  }

  return (
    <Select
      inputProps={{ 'aria-label': accessibilityLabel(columnDef) }}
      multiple
      open={filtersMenuOpen}
      onOpen={() => setFiltersMenuOpen(true)}
      onClose={() => setFiltersMenuOpen(false)}
      onSelect={() => setFiltersMenuOpen(!filtersMenuOpen)}
      renderValue={(values) => {
        if (!isArrayOfSelectableFilters(values)) {
          return '';
        }
        return renderValues(values);
      }}
      size="small"
      style={{ width: '100%' }}
      value={Object.entries(appliedFilters)}
      variant="standard"
    >
      {options.map(([k, label]) => {
        const key = k as V;
        const filterApplied = appliedFilters[key];
        return (
          <MenuItem
            key={key}
            onChange={() => toggleFilter(key)}
            onClick={() => toggleFilter(key)}
            onSelect={() => toggleFilter(key)}
            selected={filterApplied}
          >
            <ListItemIcon style={{ minWidth: '40px' }}>
              <CheckboxWrapper>
                <Checkbox
                  checked={filterApplied}
                  disableRipple
                  edge="start"
                  onChange={() => toggleFilter(key)}
                  size="small"
                />
              </CheckboxWrapper>
            </ListItemIcon>
            <Typography variant="inherit" noWrap>
              {label}
            </Typography>
          </MenuItem>
        );
      })}
    </Select>
  );
}
